import * as THREE from 'three';
import { AssetLoader } from '../loading/AssetLoader';
import {
  FOREST_INNER_RADIUS,
  FOREST_OUTER_RADIUS,
  FOREST_TREE_COUNT,
  FOREST_BUSH_COUNT,
  FOREST_ROCK_COUNT,
  FOREST_GRASS_COUNT,
  FOREST_SEED,
  POISSON_MIN_DISTANCE,
} from '../config';
import {
  forestTrees,
  forestBushes,
  forestRocks,
  forestGrass,
} from '../loading/AssetManifest';
import { createSeededRandom } from '../utils/math';

interface Point {
  x: number;
  z: number;
}

export class ForestGenerator {
  private scene: THREE.Scene;
  private loader: AssetLoader;
  private forestGroup = new THREE.Group();
  private instancedMeshes: THREE.InstancedMesh[] = [];

  constructor(scene: THREE.Scene, loader: AssetLoader) {
    this.scene = scene;
    this.loader = loader;
    scene.add(this.forestGroup);
  }

  generate() {
    const rng = createSeededRandom(FOREST_SEED);

    // Generate Poisson disc points in the ring
    const allPoints = this.poissonDiscRing(
      FOREST_INNER_RADIUS,
      FOREST_OUTER_RADIUS,
      POISSON_MIN_DISTANCE,
      rng
    );

    // Shuffle and split among categories
    this.shuffle(allPoints, rng);

    const treePoints = allPoints.slice(0, FOREST_TREE_COUNT);
    const bushPoints = allPoints.slice(FOREST_TREE_COUNT, FOREST_TREE_COUNT + FOREST_BUSH_COUNT);
    const rockPoints = allPoints.slice(
      FOREST_TREE_COUNT + FOREST_BUSH_COUNT,
      FOREST_TREE_COUNT + FOREST_BUSH_COUNT + FOREST_ROCK_COUNT
    );
    const grassPoints = allPoints.slice(
      FOREST_TREE_COUNT + FOREST_BUSH_COUNT + FOREST_ROCK_COUNT,
      FOREST_TREE_COUNT + FOREST_BUSH_COUNT + FOREST_ROCK_COUNT + FOREST_GRASS_COUNT
    );

    // Place using InstancedMesh for performance
    this.placeInstanced(treePoints, forestTrees, 'forest', rng, 0.8, 1.6);
    this.placeInstanced(bushPoints, forestBushes, 'forest', rng, 0.6, 1.2);
    this.placeInstanced(rockPoints, forestRocks, 'forest', rng, 0.5, 1.5);
    this.placeInstanced(grassPoints, forestGrass, 'forest', rng, 0.3, 0.8);
  }

  private placeInstanced(
    points: Point[],
    variants: string[],
    category: string,
    rng: () => number,
    minScale: number,
    maxScale: number
  ) {
    if (points.length === 0 || variants.length === 0) return;

    // Group points by variant
    const groups = new Map<string, Point[]>();
    for (const p of points) {
      const variant = variants[Math.floor(rng() * variants.length)];
      if (!groups.has(variant)) groups.set(variant, []);
      groups.get(variant)!.push(p);
    }

    for (const [variant, pts] of groups) {
      const key = `${category}/${variant}`;
      const gltf = this.loader.getOriginal(key);
      if (!gltf) continue;

      // Find geometry and material from the original
      let sourceGeometry: THREE.BufferGeometry | null = null;
      let sourceMaterial: THREE.Material | THREE.Material[] | null = null;

      gltf.scene.traverse((child) => {
        if (child instanceof THREE.Mesh && !sourceGeometry) {
          sourceGeometry = child.geometry;
          sourceMaterial = child.material;
        }
      });

      if (!sourceGeometry || !sourceMaterial) continue;

      const mat = Array.isArray(sourceMaterial) ? sourceMaterial[0] : sourceMaterial;
      const instancedMesh = new THREE.InstancedMesh(sourceGeometry, mat, pts.length);
      instancedMesh.castShadow = true;
      instancedMesh.receiveShadow = true;

      const dummy = new THREE.Object3D();
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        const scale = minScale + rng() * (maxScale - minScale);
        dummy.position.set(p.x, 0, p.z);
        dummy.rotation.set(0, rng() * Math.PI * 2, 0);
        dummy.scale.setScalar(scale);
        dummy.updateMatrix();
        instancedMesh.setMatrixAt(i, dummy.matrix);
      }

      instancedMesh.instanceMatrix.needsUpdate = true;
      this.forestGroup.add(instancedMesh);
      this.instancedMeshes.push(instancedMesh);
    }
  }

  private poissonDiscRing(
    innerR: number,
    outerR: number,
    minDist: number,
    rng: () => number
  ): Point[] {
    const points: Point[] = [];
    const cellSize = minDist / Math.SQRT2;
    const gridExtent = outerR * 2;
    const gridCols = Math.ceil(gridExtent / cellSize);
    const grid = new Array(gridCols * gridCols).fill(-1);

    const toGrid = (v: number) => Math.floor((v + outerR) / cellSize);

    const addPoint = (p: Point) => {
      points.push(p);
      const gx = toGrid(p.x);
      const gz = toGrid(p.z);
      if (gx >= 0 && gx < gridCols && gz >= 0 && gz < gridCols) {
        grid[gz * gridCols + gx] = points.length - 1;
      }
    };

    const isValid = (p: Point): boolean => {
      // Square exclusion for the city (city is a square, not a circle)
      if (Math.abs(p.x) < innerR && Math.abs(p.z) < innerR) return false;
      // Circular outer boundary
      const dist = Math.sqrt(p.x * p.x + p.z * p.z);
      if (dist > outerR) return false;

      const gx = toGrid(p.x);
      const gz = toGrid(p.z);
      const searchRadius = 2;

      for (let dz = -searchRadius; dz <= searchRadius; dz++) {
        for (let dx = -searchRadius; dx <= searchRadius; dx++) {
          const nx = gx + dx;
          const nz = gz + dz;
          if (nx < 0 || nx >= gridCols || nz < 0 || nz >= gridCols) continue;
          const idx = grid[nz * gridCols + nx];
          if (idx === -1) continue;
          const other = points[idx];
          const ddx = p.x - other.x;
          const ddz = p.z - other.z;
          if (ddx * ddx + ddz * ddz < minDist * minDist) return false;
        }
      }
      return true;
    };

    // Seed first point
    const angle0 = rng() * Math.PI * 2;
    const r0 = innerR + (outerR - innerR) * 0.5;
    const seed: Point = { x: Math.cos(angle0) * r0, z: Math.sin(angle0) * r0 };
    addPoint(seed);

    const active = [0];
    const maxAttempts = 30;

    while (active.length > 0) {
      const idx = Math.floor(rng() * active.length);
      const base = points[active[idx]];
      let found = false;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const angle = rng() * Math.PI * 2;
        const dist = minDist + rng() * minDist;
        const candidate: Point = {
          x: base.x + Math.cos(angle) * dist,
          z: base.z + Math.sin(angle) * dist,
        };

        if (isValid(candidate)) {
          addPoint(candidate);
          active.push(points.length - 1);
          found = true;
          break;
        }
      }

      if (!found) {
        active.splice(idx, 1);
      }
    }

    return points;
  }

  private shuffle(arr: Point[], rng: () => number) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }
}
